/**
 * Informational Sections Manager
 * Manages all informational sections on the homepage
 */

import { PopularDestinationsSectionComponent } from './PopularDestinationsSection';
import { WeatherSectionComponent } from './WeatherSection';
import { TravelTipsSectionComponent } from './TravelTipsSection';
import { EnhancedOffersSectionComponent } from './EnhancedOffersSection';
import { accessibilityEnhancer } from '../services/accessibility-enhancement-service.js';

export class InformationalSectionsManager {
  private popularDestinationsSection: PopularDestinationsSectionComponent | null = null;
  private weatherSection: WeatherSectionComponent | null = null;
  private travelTipsSection: TravelTipsSectionComponent | null = null;
  private enhancedOffersSection: EnhancedOffersSectionComponent | null = null;

  /**
   * Initialize all informational sections
   */
  public init(): void {
    try {
      console.log('🏛️ Initializing informational sections...');

      // Initialize Popular Destinations Section
      this.popularDestinationsSection = new PopularDestinationsSectionComponent();
      this.popularDestinationsSection.init();
      
      // Enhance accessibility for popular destinations
      const popularDestinationsContainer = document.querySelector('.popular-destinations-section');
      if (popularDestinationsContainer) {
        accessibilityEnhancer.enhancePopularDestinationsSection(popularDestinationsContainer as HTMLElement);
      }

      // Initialize Weather Section
      this.weatherSection = new WeatherSectionComponent();
      this.weatherSection.init();
      
      // Enhance accessibility for weather section
      const weatherContainer = document.querySelector('.weather-section');
      if (weatherContainer) {
        accessibilityEnhancer.enhanceWeatherSection(weatherContainer as HTMLElement);
      }

      // Initialize Travel Tips Section
      this.travelTipsSection = new TravelTipsSectionComponent();
      this.travelTipsSection.init();
      
      // Enhance accessibility for travel tips section
      const travelTipsContainer = document.querySelector('.travel-tips-section');
      if (travelTipsContainer) {
        accessibilityEnhancer.enhanceTravelTipsSection(travelTipsContainer as HTMLElement);
      }

      // Initialize Enhanced Offers Section in sidebar mode
      this.enhancedOffersSection = new EnhancedOffersSectionComponent(
        'Limited Special Offers',
        'Take advantage of our best offers with validity dates and detailed conditions',
        3,
        true,
        true,
        true // Enable sidebar mode
      );
      this.enhancedOffersSection.init();
      
      // Enhance accessibility for offers section
      const offersContainer = document.querySelector('.enhanced-offers-section, .special-offers-section');
      if (offersContainer) {
        accessibilityEnhancer.enhanceSpecialOffersSection(offersContainer as HTMLElement);
      }

      console.log('✅ Informational sections initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize informational sections:', error);
    }
  }

  /**
   * Refresh all sections with new data
   */
  public refreshAllSections(): void {
    try {
      if (this.weatherSection) {
        // Weather section has auto-refresh, but we can trigger manual refresh
        console.log('🔄 Refreshing weather data...');
      }

      console.log('✅ All sections refreshed');
    } catch (error) {
      console.error('❌ Failed to refresh sections:', error);
    }
  }

  /**
   * Get section by type
   */
  public getSection(type: 'destinations' | 'weather' | 'tips' | 'offers') {
    switch (type) {
      case 'destinations':
        return this.popularDestinationsSection;
      case 'weather':
        return this.weatherSection;
      case 'tips':
        return this.travelTipsSection;
      case 'offers':
        return this.enhancedOffersSection;
      default:
        return null;
    }
  }

  /**
   * Destroy all sections and clean up resources
   */
  public destroy(): void {
    if (this.popularDestinationsSection) {
      this.popularDestinationsSection.destroy();
      this.popularDestinationsSection = null;
    }

    if (this.weatherSection) {
      this.weatherSection.destroy();
      this.weatherSection = null;
    }

    if (this.travelTipsSection) {
      this.travelTipsSection.destroy();
      this.travelTipsSection = null;
    }

    if (this.enhancedOffersSection) {
      this.enhancedOffersSection.destroy();
      this.enhancedOffersSection = null;
    }

    console.log('🧹 Informational sections cleaned up');
  }
}